import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import {
  fetchProdutos,
  fetchProdutoById,
  searchProdutos,
  createProduto,
  updateProduto,
  deleteProduto,
  type ProdutoListFiltros,
} from "../services/produtoService";
import type { CriarProdutoRequest } from "../types";
import { toast } from "sonner";

// ── Tipos ─────────────────────────────────────────────────────────────────────

interface ProdutoListParams {
  pagina: number;
  tamanhoPagina: number;
  filtros?: ProdutoListFiltros;
}

// ── Hook: lista paginada ──────────────────────────────────────────────────────

export function useProdutos(
  params: ProdutoListParams = { pagina: 1, tamanhoPagina: 20 },
) {
  const query = useQuery({
    queryKey: ["produtos", params.pagina, params.tamanhoPagina, params.filtros ?? {}],
    queryFn: () => fetchProdutos(params.pagina, params.tamanhoPagina, params.filtros),
    // Mantém a página anterior na tela enquanto o novo filtro/ordem carrega.
    placeholderData: keepPreviousData,
  });

  return {
    produtos:      query.data?.itens ?? [],
    total:         query.data?.total ?? 0,
    isLoading:     query.isLoading,
    isFetching:    query.isFetching,
    error:         query.error,
  };
}

// ── Hook: busca por termo ─────────────────────────────────────────────────────

export function useProdutoSearch(termo: string, enabled: boolean) {
  return useQuery({
    queryKey: ["produtos-search", termo],
    queryFn: () => searchProdutos(termo),
    enabled: enabled && termo.trim().length >= 2,
  });
}

// ── Hook: detalhe por ID ──────────────────────────────────────────────────────

export function useProduto(id: string | undefined) {
  return useQuery({
    queryKey: ["produtos", id],
    queryFn: () => fetchProdutoById(id!),
    enabled: !!id,
  });
}

// ── Mutation: criar ───────────────────────────────────────────────────────────

export function useCreateProduto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CriarProdutoRequest) => createProduto(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["produtos"] });
      toast.success("Produto cadastrado com sucesso!");
    },
    onError: (error: Error) => {
      toast.error(`Erro ao cadastrar produto: ${error.message}`);
    },
  });
}

// ── Mutation: atualizar ───────────────────────────────────────────────────────

export function useUpdateProduto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CriarProdutoRequest> }) =>
      updateProduto(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["produtos"] });
      toast.success("Produto atualizado com sucesso!");
    },
    onError: (error: Error) => {
      toast.error(`Erro ao atualizar produto: ${error.message}`);
    },
  });
}

// ── Mutation: excluir ─────────────────────────────────────────────────────────

export function useDeleteProduto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteProduto(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["produtos"] });
      toast.success("Produto excluído com sucesso!");
    },
    onError: (error: Error) => {
      toast.error(`Erro ao excluir produto: ${error.message}`);
    },
  });
}
